// UserGroups.tsx
import React, { useState, useContext, useMemo } from "react";
import { Table, Button, Space, Input, message, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";

import { UIAssetFilterDTO, UserGroupDTO, UserViewDTO } from "../../backendapi/requests";
import CreateUserGroup from "./CreateUserGroup";
import EditUserGroup from "./EditUserGroup";
import { useUiAssetFilterServiceGetBackendv2Uiassetfilter, useUiAssetFilterServiceGetBackendv2Uiassetfilter1Key, useUserGroupServiceDeleteBackendv2Usergroup, useUserGroupServiceGetBackendv2Usergroup, useUserGroupServiceGetBackendv2UsergroupKey, useUserServiceGetBackendv2User, useUserServiceGetBackendv2UserKey } from "../../backendapi/queries";
import { ConfigContext } from "../../contexts/ConfigContext";
import { GenerateODataFilter } from "../../helpers/odataFunctions";
import { useDebounce } from "use-debounce";
import { Filter } from "../../types/types";
import { useQueryClient } from "@tanstack/react-query";

const UserGroups: React.FC = () => {
  const queryClient = useQueryClient();

  const { currentClient } = useContext(ConfigContext);

  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 500);

  const [ messageApi, contextHolder ] = message.useMessage();

  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroupDTO | null>(null);
  const [sortField, setSortField] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  // Pagination state
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total) => `Total ${total} items`,
  });

  const filters = useMemo(() => {
    let filters: Filter[] = [];

    if (debouncedQuery) {
      filters.push({ type: "search", values: [{ value: debouncedQuery }], property: "name", name: "name" });
    }

    return filters;
  }, [debouncedQuery, currentClient]);

  const { data: userGroups, isFetching: loading } = useUserGroupServiceGetBackendv2Usergroup(
    {
      clientId: currentClient.id,
      top: pagination.pageSize?.toString(),
      skip: (((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 1)).toString(),
      filter: GenerateODataFilter(filters),
      orderby: `${sortField} ${sortOrder}`,
      count: "true",
    },
    [useUserGroupServiceGetBackendv2UsergroupKey, currentClient.id, filters],
    { refetchOnWindowFocus: false, placeholderData: (prev) => prev }
  );
  
  const { mutateAsync: deleteUserGroup } = useUserGroupServiceDeleteBackendv2Usergroup();

 // const { data: availableFolders } = useOrderFolderServiceGetBackendv2Orderfolder({ clientId: currentClient.id }, [useOrderFolderServiceGetBackendv2OrderfolderKey], { refetchOnWindowFocus: false, enabled: !!currentClient.id });
  const { data: availableUsers } = useUserServiceGetBackendv2User({ clientId: currentClient.id }, [useUserServiceGetBackendv2UserKey, currentClient.id], { refetchOnWindowFocus: false, enabled: !!currentClient.id});
  const { data: availableFilters } = useUiAssetFilterServiceGetBackendv2Uiassetfilter({ clientId: currentClient.id}, [useUiAssetFilterServiceGetBackendv2Uiassetfilter1Key, currentClient.id], { refetchOnWindowFocus: false, enabled: !!currentClient.id})

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = async (newPagination: TablePaginationConfig) => {
    await queryClient.invalidateQueries({ queryKey: [useUserGroupServiceGetBackendv2UsergroupKey] });
    setPagination({ ...newPagination, total: userGroups?.value.length });   
  };

   const handleSearch = async (value: string) => {
        setQuery(value);       
    };

  const showEditDrawer = (group: UserGroupDTO) => {
    setEditingGroup(group);
    setIsEditDrawerVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {     
      await deleteUserGroup({ key: id });

      messageApi.success("User group deleted");
      await queryClient.invalidateQueries({ queryKey: [useUserGroupServiceGetBackendv2UsergroupKey] });
    } catch (error) {
      messageApi.error("Failed to delete user group");
    }
  };

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: [useUserGroupServiceGetBackendv2UsergroupKey] });
  };

  // Table columns
  const columns: ColumnsType<UserGroupDTO> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    }, 
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      render: (users: UserViewDTO[]) => users?.length ?? 0,
    },
     {
      title: "Filters",
      dataIndex: "filters",
      key: "filters",
      render: (filters: UIAssetFilterDTO[]) => filters?.length ?? 0,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => showEditDrawer(record)}>
            Edit
          </Button>
          <Popconfirm title="Are you sure you want to delete this user group?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button type="link">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <Input placeholder="Search" prefix={<SearchOutlined />} allowClear style={{ maxWidth: 400 }} onChange={(e) => handleSearch(e.target.value)} />
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateDrawerVisible(true)}>
            Add User Group
          </Button>
        </div>

        <Table columns={columns} dataSource={userGroups?.value} rowKey="id" loading={loading} pagination={pagination} onChange={handleTableChange} scroll={{ x: 1000 }} />

        <CreateUserGroup visible={isCreateDrawerVisible} onClose={() => setIsCreateDrawerVisible(false)} onSuccess={handleSuccess} availableUsers={availableUsers?.value ?? []} availableFilters={availableFilters?.value ?? []} />

        {editingGroup && (
          <EditUserGroup
            visible={isEditDrawerVisible}
            onClose={() => {
              setIsEditDrawerVisible(false);
              setEditingGroup(null);
            }}
            onSuccess={handleSuccess}
            userGroup={editingGroup}           
            availableUsers={availableUsers?.value ?? []}
            availableFilters={availableFilters?.value ?? []}
          />
        )}
      </Space>
    </>
  );
};

export default UserGroups;
